import type { Query, QueryParameterValue } from './types.js'

import { enumerateFieldMatcherNodes, isExactFieldMatch } from './match.js'
import { isRelativeDate, parseDateRange } from './parse-date.js'

/**
 * Replace date math (`now`, `NOW-1d`, `now/d`, ...) in unquoted terms and
 * range bounds with ISO dates relative to `now`. Quoted terms (`isStrict`,
 * `data.name:"now"`) are exact values and stay as they are, as does any value
 * that is not a whole date expression. Returns a copy; `query` is unchanged.
 */
export function rewriteRelativeDates(query: Query, now: string): Query {
  const exact = new Set<number|string>()
  const relative = new Set<number|string>()

  for (const fieldMatch of enumerateFieldMatcherNodes(query)) {
    if (fieldMatch.term) {
      (isExactFieldMatch(fieldMatch) ? exact : relative).add(fieldMatch.term.id)
    }
    if (fieldMatch.term_min) relative.add(fieldMatch.term_min.id)
    if (fieldMatch.term_max) relative.add(fieldMatch.term_max.id)
  }

  const toDate = (value: QueryParameterValue) => isRelativeDate(value) ? parseDateRange(value, { now }).toISO() : value
  const parameters: any = Array.isArray(query.parameters) ? [...query.parameters] : { ...query.parameters }

  for (const id of relative) {
    if (exact.has(id)) continue
    const value = parameters[id]
    parameters[id] = Array.isArray(value) ? value.map(toDate) : toDate(value)
  }

  return { ...query, parameters }
}
